import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DollarSign, Calendar, Users, TrendingUp, ArrowUpRight, ChevronRight
} from 'lucide-react';

export const OverviewTab = ({ analytics, bookings, venues, theme, onTabChange }) => {
  const { cardBg, cardBorder, innerCardBg, hoverBg } = theme;
  const recentBookings = bookings.slice(0, 5);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30';
      case 'pending': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-500 border-red-500/30';
      case 'completed': return 'bg-primary/20 text-primary border-primary/30';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  return (
    <div className="space-y-8" data-testid="overview-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-colors`}>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-2 break-words">
                  {(analytics?.total_revenue || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </p>
                <div className="flex items-center text-xs text-emerald-500 mt-2">
                  <ArrowUpRight className="w-3 h-3 mr-1" />+12.5% from last month
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <DollarSign className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-colors`}>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground">Total Bookings</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-2 break-words">{analytics?.total_bookings || 0}</p>
                <div className="flex items-center text-xs text-emerald-500 mt-2">
                  <ArrowUpRight className="w-3 h-3 mr-1" />+8.2% from last month
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <Calendar className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-colors`}>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-2 break-words">{analytics?.total_users || 0}</p>
                <div className="flex items-center text-xs text-emerald-500 mt-2">
                  <ArrowUpRight className="w-3 h-3 mr-1" />+15.3% from last month
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0">
                <Users className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm text-primary/80">Today's Bookings</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-2 text-primary break-words">{analytics?.todays_bookings || 0}</p>
                <p className="text-xs text-primary/60 mt-2">{analytics?.confirmed_bookings || 0} confirmed</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className={`${cardBg} ${cardBorder}`}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading">Recent Bookings</CardTitle>
            <Button variant="ghost" size="sm" className="text-primary" onClick={() => onTabChange('bookings')}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentBookings.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No bookings yet</p>
              ) : (
                recentBookings.map(booking => (
                  <div key={booking.id} className={`flex items-center justify-between p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
                    <div className="space-y-1">
                      <p className="font-mono text-sm">#{booking.id?.slice(0, 8)}</p>
                      <p className="text-xs text-muted-foreground">{booking.booking_date} &bull; {booking.start_time}</p>
                    </div>
                    <Badge className={`${getStatusColor(booking.status)} border`}>{booking.status}</Badge>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card className={`${cardBg} ${cardBorder}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading">Booking Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(analytics?.bookings_by_status || {}).map(([status, count]) => {
                const total = Object.values(analytics?.bookings_by_status || {}).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? (count / total) * 100 : 0;
                return (
                  <div key={status} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge className={`${getStatusColor(status)} border`}>{status}</Badge>
                      <span className="font-heading font-semibold">{count}</span>
                    </div>
                    <div className={`h-2 ${innerCardBg} rounded-full overflow-hidden`}>
                      <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className={`${cardBg} ${cardBorder}`}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-heading">Active Venues ({venues.length})</CardTitle>
          <Button variant="ghost" size="sm" className="text-primary" onClick={() => onTabChange('venues')}>
            Manage <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {venues.map(venue => (
              <div key={venue.id} className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder} hover:border-primary/30 transition-all ${!venue.is_active ? 'opacity-50 grayscale' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 overflow-hidden shrink-0">
                    <img
                      src={venue.images?.[0] ? (venue.images[0].startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${venue.images[0]}` : venue.images[0]) : 'https://images.unsplash.com/photo-1497366412874-3415097a27e7?w=200'}
                      alt={venue.name} className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1497366412874-3415097a27e7?w=200'; }}
                    />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-medium truncate">{venue.name}</h4>
                    <p className="text-sm text-muted-foreground">{venue.city}</p>
                    <Badge variant={venue.is_active ? 'default' : 'secondary'} className="mt-2 text-xs">
                      {venue.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
